import React, { useState, useEffect } from "react";
import { FiSettings, FiEye, FiEyeOff, FiSave, FiRefreshCw, FiEdit3, FiX, FiCalendar } from "react-icons/fi";
import { toast } from "react-toastify";
import { useAuth } from "../context/AuthContext";

const Platform = () => {
  const { user } = useAuth();

  console.log(user);
  
  // Check if user is primary type
  const isPrimaryUser = user?.isPrimary;
  
  // Dialog state
  const [showDialog, setShowDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  
  // Amazon credentials state
  const [amazonCredentials, setAmazonCredentials] = useState({
    client_id: "",
    client_secret: "",
    refresh_token: ""
  });
  
  const [showCredentials, setShowCredentials] = useState({
    client_secret: false,
    refresh_token: false,
  });
  
  const [credentialsLoading, setCredentialsLoading] = useState(false);
  const [expiryDate, setExpiryDate] = useState(null);
  const [dateLoading, setDateLoading] = useState(true);

  // Fetch expiry date
  const fetchExpiryDate = async () => {
    try {
      setDateLoading(true);
      const baseURL = process.env.REACT_APP_API_BASE_URL || 'https://digitalapi.palsonsderma.com';
      const response = await fetch(`${baseURL}/entity/admin/amazon-credentials/expiration-date`, {
        method: 'GET',
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        if (data.status && data.data) {
          setExpiryDate(data.data);
        }
      }
    } catch (error) {
      console.error('Error fetching expiry date:', error);
    } finally {
      setDateLoading(false);
    }
  };

  // Load expiry date on component mount
  useEffect(() => {
    fetchExpiryDate();
  }, []);

  // Amazon credentials handlers
  const handleCredentialsChange = (field, value) => {
    setAmazonCredentials(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const toggleCredentialVisibility = (field) => {
    setShowCredentials(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    
    if (!isPrimaryUser) {
      toast.error('Only admin can update credentials');
      return;
    }
    
    // Show confirmation dialog instead of directly submitting
    setShowConfirmDialog(true);
  };

  const handleConfirmUpdate = async () => {
    setCredentialsLoading(true);
    setShowConfirmDialog(false);
    
    try {
      const baseURL = process.env.REACT_APP_API_BASE_URL || 'https://digitalapi.palsonsderma.com';
      const response = await fetch(`${baseURL}/entity/admin/amazon-credentials`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(amazonCredentials)
      });

      if (response.ok) {
        toast.success('Amazon credentials updated successfully!');
        setShowDialog(false);
        // Refresh the expiry date
        fetchExpiryDate();
      } else {
        throw new Error('Failed to update credentials');
      }
    } catch (error) {
      console.error('Error updating Amazon credentials:', error);
      toast.error('Failed to update Amazon credentials. Please try again.');
    } finally {
      setCredentialsLoading(false);
    }
  };

  const handleCancelUpdate = () => {
    setShowConfirmDialog(false);
  };

  const openDialog = () => {
    if (!isPrimaryUser) {
      toast.error('Only Admin can update credentials');
      return;
    }
    setShowDialog(true);
  };

  const closeDialog = () => {
    setShowDialog(false);
    // Reset visibility states when closing dialog
    setShowCredentials({
      client_secret: false,
      refresh_token: false,
    });
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Platform Management</h1>
          <p className="text-gray-600 mt-1">Manage platform integrations and API credentials</p>
        </div>
      </div>

      {/* Amazon Credentials Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <FiSettings className="text-orange-600" size={20} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Amazon API Credentials</h3>
              <p className="text-sm text-gray-600">Configure your Amazon API credentials for integration</p>
            </div>
          </div>
          <button
            onClick={openDialog}
            className={`btn btn-primary btn-sm flex items-center gap-2 ${
              !isPrimaryUser ? 'btn-disabled' : ''
            }`}
            disabled={!isPrimaryUser}
          >
            <FiEdit3 size={16} />
            Update Credentials
          </button>
        </div>
        
        {/* Credentials Status */}
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <div className="flex-1">
              <h4 className="font-medium text-green-900">Amazon API Credentials Configured</h4>
              <p className="text-sm text-green-700">Your Amazon API credentials are set up and ready for integration.</p>
              {dateLoading ? (
                <div className="flex items-center gap-2 mt-2">
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-green-600"></div>
                  <span className="text-xs text-green-600">Loading expiry date...</span>
                </div>
              ) : expiryDate ? (
                <div className="flex items-center gap-2 mt-2">
                  <FiCalendar className="text-green-600" size={12} />
                  <span className="text-xs text-green-600">
                    Expires: {formatDate(expiryDate)}
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2 mt-2">
                  <FiCalendar className="text-green-600" size={12} />
                  <span className="text-xs text-green-600">
                    Expiry date: Not available
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {!isPrimaryUser && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> Only admin can update credentials. Contact your administrator for access.
            </p>
          </div>
        )}
      </div>

      {/* Additional Platform Information */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="p-1 bg-blue-100 rounded">
            <FiSettings className="text-blue-600" size={16} />
          </div>
          <div>
            <h4 className="font-medium text-blue-900 mb-1">Platform Integration Status</h4>
            <p className="text-sm text-blue-700">
              Your Amazon API credentials are used to sync data from Amazon Seller Central. 
              Make sure to keep these credentials secure and up-to-date for proper integration.
            </p>
          </div>
        </div>
      </div>

      {/* Credentials Update Dialog */}
      {showDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10001] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Update Amazon Credentials</h3>
              <button
                onClick={closeDialog}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <FiX size={20} />
              </button>
            </div>
            
            <form onSubmit={handleCredentialsSubmit} className="p-6 space-y-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-medium">Client ID</span>
                </label>
                <input
                  type="text"
                  value={amazonCredentials.client_id}
                  onChange={(e) => handleCredentialsChange('client_id', e.target.value)}
                  className="input input-bordered w-full"
                  required
                />
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text font-medium">Client Secret</span>
                </label>
                <div className="relative">
                  <input
                    type={showCredentials.client_secret ? "text" : "password"}
                    value={amazonCredentials.client_secret}
                    onChange={(e) => handleCredentialsChange('client_secret', e.target.value)}
                    className="input input-bordered w-full pr-12"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => toggleCredentialVisibility('client_secret')}
                    className="absolute inset-y-0 right-0 flex items-center pr-3"
                  >
                    {showCredentials.client_secret ? (
                      <FiEyeOff className="text-gray-400 hover:text-gray-600" />
                    ) : (
                      <FiEye className="text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text font-medium">Refresh Token</span>
                </label>
                <div className="relative">
                  <textarea
                    value={amazonCredentials.refresh_token}
                    onChange={(e) => handleCredentialsChange('refresh_token', e.target.value)}
                    className="textarea textarea-bordered w-full pr-12 min-h-[100px]"
                  required
                  />
                  <button
                    type="button"
                    onClick={() => toggleCredentialVisibility('refresh_token')}
                    className="absolute top-3 right-3"
                  >
                    {showCredentials.refresh_token ? (
                      <FiEyeOff className="text-gray-400 hover:text-gray-600" />
                    ) : (
                      <FiEye className="text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={closeDialog}
                  className="btn btn-outline"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={credentialsLoading}
                >
                  {credentialsLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Updating...
                    </>
                  ) : (
                    <>
                      <FiSave size={16} className="mr-2" />
                      Update Credentials
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10001] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <FiSettings className="text-yellow-600" size={20} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Confirm Update</h3>
              </div>
              
              <p className="text-gray-600 mb-6">
                Are you sure you want to update the Amazon API credentials? This action will replace the current credentials and may affect the integration.
              </p>
              
              <div className="flex justify-end gap-3">
                <button
                  onClick={handleCancelUpdate}
                  className="btn btn-outline"
                  disabled={credentialsLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmUpdate}
                  className="btn btn-warning"
                  disabled={credentialsLoading}
                >
                  {credentialsLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Updating...
                    </>
                  ) : (
                    'Confirm Update'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Platform;
